import React, { useState } from 'react';
import { AlertTriangleIcon, XIcon } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line, ComposedChart } from 'recharts';
interface TrendAlert {
  id: string;
  title: string;
  description: string;
  details?: string;
  mapCenter?: [number, number];
  locations?: Array<{
    id: string;
    position: [number, number];
    description: string;
  }>;
  weeklyData?: Array<{
    week: string;
    failures: number;
  }>;
}
export const TrendAlerts: React.FC = () => {
  const [alerts, setAlerts] = useState<TrendAlert[]>([{
    id: 'sprockets',
    title: 'New Trend Detected',
    description: 'Sprockets break down in area cluster',
    details: '8 sprockets broke down in this area in the past 7 days. Preliminary analysis suggests the failures may be associated with environmental or operational factors specific to this geographic region.',
    mapCenter: [29.183547, -100.714560],
    locations: [{
      id: 'sp1',
      position: [29.1840, -100.7162],
      description: 'Sprocket failure on roadside pullout along US 277'
    }, {
      id: 'sp2',
      position: [29.1822, -100.7128],
      description: 'Sprocket failure near creekbed off rural access trail'
    }, {
      id: 'sp3',
      position: [29.1851, -100.7131],
      description: 'Sprocket failure beside utility easement'
    }, {
      id: 'sp4',
      position: [29.1819, -100.7170],
      description: 'Sprocket failure near livestock gate trail'
    }, {
      id: 'sp5',
      position: [29.1837, -100.7119],
      description: 'Sprocket failure on dirt maintenance path near Sycamore Creek'
    }, {
      id: 'sp6',
      position: [29.1765, -100.7225],
      description: 'Sprocket failure on ranch road bend near Sycamore draw'
    }, {
      id: 'sp7',
      position: [29.1898, -100.7095],
      description: 'Sprocket failure near high ground overlook'
    }, {
      id: 'sp8',
      position: [29.1923, -100.7210],
      description: 'Sprocket failure along old fence line road'
    }]
  }, {
    id: 'air-filters',
    title: 'New Trend Detected',
    description: 'Rising Failure Rate in Humvee Air Filter Assemblies',
    details: '12 air filter failures reported in the past 10 days. Analysis indicates potential contamination from fine dust in the region.',
    mapCenter: [26.3761, -98.8089],
    locations: [{
      id: 'af1',
      position: [26.3761, -98.8089],
      description: 'Air filter failure in Rio Grande City'
    }, {
      id: 'af2',
      position: [26.3861, -98.7889],
      description: 'Air filter failure on US-83'
    }, {
      id: 'af3',
      position: [26.3661, -98.8289],
      description: 'Air filter failure near La Puerta'
    }, {
      id: 'af4',
      position: [26.3961, -98.8389],
      description: 'Air filter failure on FM 755'
    }, {
      id: 'af5',
      position: [26.3561, -98.7789],
      description: 'Air filter failure on FM 3167'
    }, {
      id: 'af6',
      position: [26.3861, -98.8589],
      description: 'Air filter failure near Roma'
    }],
    weeklyData: [{
      week: 'Week 1',
      failures: 1
    }, {
      week: 'Week 2',
      failures: 0
    }, {
      week: 'Week 3',
      failures: 1
    }, {
      week: 'Week 4',
      failures: 0
    }, {
      week: 'Week 5',
      failures: 3
    }, {
      week: 'Week 6',
      failures: 5
    }, {
      week: 'Week 7',
      failures: 7
    }]
  }]);
  const [selectedAlert, setSelectedAlert] = useState<TrendAlert | null>(null);
  const dismissAlert = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setAlerts(alerts.filter(alert => alert.id !== id));
    if (selectedAlert?.id === id) {
      setSelectedAlert(null);
    }
  };
  const openAnalysisDialog = (alert: TrendAlert) => {
    setSelectedAlert(alert);
  };
  const closeAnalysisDialog = () => {
    setSelectedAlert(null);
  };
  // Create custom icon for map markers
  const createCustomIcon = (color = '#F59E0B') => {
    return L.divIcon({
      html: `<div style="background-color: ${color}; width: 12px; height: 12px; display: flex; border-radius: 50%; border: 2px solid white;"></div>`,
      className: '',
      iconSize: [16, 16],
      iconAnchor: [8, 8]
    });
  };
  if (alerts.length === 0) return null;
  return <div className="mb-6 flex flex-row gap-3" data-id="element-1020">
      {alerts.map(alert => <div key={alert.id} className={`flex items-start p-4 border rounded-md cursor-pointer transition-colors w-1/2 ${alert.id === 'sprockets' ? 'bg-amber-50 border-amber-200 hover:bg-amber-100' : 'bg-amber-50 border-amber-200 hover:bg-amber-100'}`} onClick={() => openAnalysisDialog(alert)} data-id="element-1021">
          <AlertTriangleIcon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${alert.id === 'sprockets' ? 'text-amber-500' : 'text-amber-500'}`} data-id="element-1022" />
          <div className="ml-3 flex-grow" data-id="element-1023">
            <h3 className={`text-sm font-medium ${alert.id === 'sprockets' ? 'text-amber-800' : 'text-amber-800'}`} data-id="element-1024">
              {alert.title}
            </h3>
            <div className={`mt-1 text-sm ${alert.id === 'sprockets' ? 'text-amber-700' : 'text-amber-700'}`} data-id="element-1025">
              {alert.description}
            </div>
          </div>
          <button onClick={e => dismissAlert(alert.id, e)} className={`ml-auto flex-shrink-0 ${alert.id === 'sprockets' ? 'text-amber-500 hover:text-amber-700' : 'text-amber-500 hover:text-amber-700'}`} data-id="element-1026">
            <XIcon className="h-4 w-4" data-id="element-1027" />
          </button>
        </div>)}
      {selectedAlert && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1028">
          <div className={`bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto ${selectedAlert.id === 'air-filters' ? 'border-t-4 border-t-red-500' : 'border-t-4 border-t-amber-500'}`} data-id="element-1029">
            <div className="flex justify-between items-center border-b p-4" data-id="element-1030">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center" data-id="element-1031">
                <AlertTriangleIcon className={`h-5 w-5 mr-2 ${selectedAlert.id === 'air-filters' ? 'text-red-500' : 'text-amber-500'}`} data-id="element-1032" />
                {selectedAlert.title}
              </h2>
              <button onClick={closeAnalysisDialog} className="text-gray-500 hover:text-gray-700" data-id="element-1033">
                <XIcon className="h-5 w-5" data-id="element-1034" />
              </button>
            </div>
            <div className="p-4" data-id="element-1035">
              <p className="text-gray-700 mb-4" data-id="element-1036">{selectedAlert.details}</p>
              {selectedAlert.id === 'air-filters' && selectedAlert.weeklyData && <div className="mb-6" data-id="element-1037">
                    <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1038">
                      Air Filter Assembly Failure Rate
                    </h3>
                    <div className="border rounded-md p-4 bg-white" data-id="element-1039">
                      <ComposedChart width={500} height={300} data={selectedAlert.weeklyData} margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-1040">
                        <CartesianGrid strokeDasharray="3 3" data-id="element-1041" />
                        <XAxis dataKey="week" data-id="element-1042" />
                        <YAxis label={{
                  value: 'Failures',
                  angle: -90,
                  position: 'insideLeft'
                }} data-id="element-1043" />
                        <Tooltip data-id="element-1044" />
                        <Legend data-id="element-1045" />
                        <Bar dataKey="failures" fill="#EF4444" name="Failures" data-id="element-1046" />
                        <Line type="monotone" dataKey="failures" stroke="#991B1B" name="Trend" strokeWidth={2} dot={{
                  r: 4
                }} data-id="element-1047" />
                      </ComposedChart>
                      <div className="mt-2 text-sm text-gray-600" data-id="element-1048">
                        <p className="font-medium text-red-600" data-id="element-1049">
                          Alert: Significant upward trend detected in the last 3
                          weeks
                        </p>
                        <p data-id="element-1050">Normal range: 0-1 failures per week</p>
                      </div>
                    </div>
                  </div>}
              {selectedAlert.mapCenter && selectedAlert.locations && <div data-id="element-1051">
                  <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1052">
                    Geographical Distribution
                  </h3>
                  <div className="h-[300px] w-full border rounded overflow-hidden" data-id="element-1053">
                    <MapContainer center={selectedAlert.mapCenter} zoom={12} style={{
                height: '100%',
                width: '100%'
              }} data-id="element-1054">
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' data-id="element-1055" />
                      {selectedAlert.locations.map(location => <Marker key={location.id} position={location.position} icon={createCustomIcon(selectedAlert.id === 'air-filters' ? '#EF4444' : '#F59E0B')} data-id="element-1056">
                          <Popup data-id="element-1057">
                            <div className="text-sm" data-id="element-1058">
                              <p className="font-medium" data-id="element-1059">
                                {location.description}
                              </p>
                              <p className="text-xs text-gray-500 mt-1" data-id="element-1060">
                                Incident reported
                              </p>
                            </div>
                          </Popup>
                        </Marker>)}
                    </MapContainer>
                  </div>
                </div>}
            </div>
            <div className={`p-4 border-t ${selectedAlert.id === 'air-filters' ? 'bg-red-50' : 'bg-amber-50'}`} data-id="element-1061">
              <div className="flex justify-between" data-id="element-1062">
                <button onClick={closeAnalysisDialog} className="px-4 py-2 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1063">
                  Close
                </button>
                <button className={`px-4 py-2 rounded text-sm font-medium text-white ${selectedAlert.id === 'air-filters' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`} data-id="element-1064">
                  View Full Report
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};